import { Injectable } from '@angular/core';
import {
  ChatbotAnswer,
  ChatbotHistoryItem,
  ChatbotKnowledgeItem,
  ChatbotMessage,
} from 'src/app/models/chatbot.model';

@Injectable({
  providedIn: 'root',
})
export class StaticChatbotService {
  private readonly messageStorageKey = 'flowdrizzle_chatbot_messages_v2';
  private readonly historyStorageKey = 'flowdrizzle_chatbot_qa_history_v2';
  private readonly maxMessages = 80;
  private readonly maxHistoryItems = 30;

  private readonly fallbackAnswer: ChatbotAnswer = {
    answer:
      'I could not find an exact answer yet. FlowDrizzle helps teams manage leads, CRM activity, sales pipelines, automation, reporting, campaigns, and customer communication. You can explore the useful pages below.',
    links: [
      {
        label: 'Home',
        url: '/home',
        description: 'View FlowDrizzle CRM features, pricing, and benefits.',
      },
      {
        label: 'Contact Sales',
        url: '/contact',
        description: 'Contact the FlowDrizzle team for demo, support, or enterprise questions.',
      },
      {
        label: 'FAQ',
        url: '/faq',
        description: 'Read common FlowDrizzle questions.',
      },
    ],
  };

  private readonly knowledgeBase: ChatbotKnowledgeItem[] = [
    {
      id: 'overview',
      question: 'What is FlowDrizzle?',
      answer:
        'FlowDrizzle is an enterprise-ready CRM platform that helps businesses capture leads, manage customers, track sales pipelines, automate follow-ups, monitor performance, and improve team productivity from one centralized system.',
      keywords: ['flowdrizzle', 'crm', 'overview', 'platform', 'customer', 'business', 'sales'],
      links: [
        { label: 'Home', url: '/home', description: 'Explore FlowDrizzle overview.' },
        { label: 'About Us', url: '/about', description: 'Learn more about FlowDrizzle.' },
      ],
    },
    {
      id: 'enterprise',
      question: 'Is FlowDrizzle suitable for enterprise teams?',
      answer:
        'Yes. FlowDrizzle is designed for growing and enterprise teams that need structured lead tracking, role-based team management, pipeline visibility, automated workflows, reporting, customer communication, and scalable CRM operations.',
      keywords: ['enterprise', 'team', 'teams', 'scale', 'scalable', 'role', 'management'],
      links: [
        { label: 'Enterprise Plan', url: '/home', description: 'Review enterprise pricing and features.' },
        { label: 'Contact Sales', url: '/contact', description: 'Talk to the team for enterprise requirements.' },
      ],
    },
    {
      id: 'features',
      question: 'What features does FlowDrizzle provide?',
      answer:
        'FlowDrizzle includes lead management, sales pipeline tracking, CRM dashboards, email campaigns, nurture journeys, lead forms, booking calendar, analytics, reporting, team management, and customer support tools.',
      keywords: ['feature', 'features', 'tools', 'lead', 'pipeline', 'automation', 'analytics', 'reporting', 'description'],
      links: [
        { label: 'Feature Overview', url: '/home', description: 'See FlowDrizzle features.' },
        { label: 'Dashboard', url: '/app/dashboard', description: 'Open CRM dashboard after login.' },
      ],
    },
    {
      id: 'lead-management',
      question: 'How does lead management work?',
      answer:
        'Lead management in FlowDrizzle helps capture, organize, assign, score, and follow up with leads. Teams can avoid missed opportunities and maintain a clear view of every prospect from inquiry to conversion.',
      keywords: ['lead', 'leads', 'capture', 'assign', 'score', 'follow', 'prospect', 'opportunity'],
      links: [
        { label: 'Generated Leads', url: '/app/lead-generated', description: 'Manage captured leads.' },
        { label: 'Form Builder', url: '/admin/form-builder', description: 'Create lead capture forms.' },
      ],
    },
    {
      id: 'pipeline',
      question: 'Can FlowDrizzle manage my sales pipeline?',
      answer:
        'Yes. FlowDrizzle provides a visual sales pipeline where teams can track deals, move opportunities across stages, assign owners, monitor activities, and improve sales forecasting.',
      keywords: ['pipeline', 'deal', 'deals', 'stage', 'sales', 'kanban', 'forecast'],
      links: [
        { label: 'Pipeline Board', url: '/app/pipeline-board', description: 'Track deals visually.' },
        { label: 'Funnel Dashboard', url: '/app/funnel-dashboard', description: 'Review conversion funnel.' },
      ],
    },
    {
      id: 'automation',
      question: 'What automation is available?',
      answer:
        'FlowDrizzle supports sales and marketing automation such as email sequences, nurture journeys, reminders, lead follow-ups, task automation, and campaign workflows.',
      keywords: ['automation', 'automate', 'workflow', 'workflows', 'email', 'sequence', 'reminder', 'nurture', 'campaign'],
      links: [
        { label: 'Email Nurture', url: '/app/email-nurture', description: 'Build automated nurture journeys.' },
        { label: 'Email Setup', url: '/app/email-setup', description: 'Configure campaigns.' },
      ],
    },
    {
      id: 'reports',
      question: 'Does FlowDrizzle include analytics and reports?',
      answer:
        'Yes. FlowDrizzle provides dashboards and reports for sales performance, funnel tracking, email campaigns, lead activity, and business insights so teams can make data-driven decisions.',
      keywords: ['analytics', 'report', 'reports', 'dashboard', 'insights', 'performance', 'funnel'],
      links: [
        { label: 'Analytics', url: '/app/analytics', description: 'View CRM analytics.' },
        { label: 'Email Reports', url: '/app/email-reports', description: 'Track campaign performance.' },
      ],
    },
    {
      id: 'pricing',
      question: 'What are the pricing plans?',
      answer:
        'FlowDrizzle pricing includes Starter, Professional, and Enterprise plans. Starter is suitable for small teams, Professional is useful for growing sales teams, and Enterprise is designed for advanced customization, scale, and dedicated support.',
      keywords: ['pricing', 'price', 'plan', 'starter', 'professional', 'enterprise', 'cost'],
      links: [
        { label: 'Pricing', url: '/home', description: 'View pricing section.' },
        { label: 'Contact Sales', url: '/contact', description: 'Ask about enterprise pricing.' },
      ],
    },
    {
      id: 'trial-demo',
      question: 'How can I start a trial or book a demo?',
      answer:
        'You can start by registering for FlowDrizzle or contacting the team for a guided demo. The demo is useful if you want to understand features, onboarding, and enterprise setup.',
      keywords: ['trial', 'demo', 'register', 'signup', 'start', 'free', 'onboarding'],
      links: [
        { label: 'Start Free Trial', url: '/authentication/register', description: 'Create your account.' },
        { label: 'Book Demo', url: '/contact', description: 'Request a guided demo.' },
      ],
    },
    {
      id: 'support',
      question: 'How do I contact support?',
      answer:
        'You can contact FlowDrizzle using the Contact Us page. Logged-in customers can also use the customer support area for product help, account support, and service questions.',
      keywords: ['support', 'help', 'contact', 'issue', 'customer', 'service'],
      links: [
        { label: 'Contact Us', url: '/contact', description: 'Send a message to the team.' },
        { label: 'Customer Support', url: '/customer/contact-support', description: 'Open support center.' },
      ],
    },
    {
      id: 'forms',
      question: 'Can I create website lead forms?',
      answer:
        'Yes. FlowDrizzle includes a form builder for creating lead capture forms. Admins can build forms, preview them, and use them to collect website inquiries directly into the CRM lead workflow.',
      keywords: ['form', 'forms', 'website', 'capture', 'inquiry', 'builder', 'preview'],
      links: [
        { label: 'Form Builder', url: '/admin/form-builder', description: 'Create and manage lead forms.' },
        { label: 'Generated Leads', url: '/app/lead-generated', description: 'Review leads collected from forms.' },
      ],
    },
    {
      id: 'calendar-bookings',
      question: 'Does FlowDrizzle support bookings and calendars?',
      answer:
        'Yes. FlowDrizzle includes a bookings calendar so teams can manage scheduled activities, appointments, and sales follow-ups in one CRM workspace.',
      keywords: ['calendar', 'calender', 'booking', 'bookings', 'appointment', 'schedule', 'scheduled'],
      links: [
        { label: 'Bookings Calendar', url: '/app/calender', description: 'Open the CRM bookings calendar.' },
      ],
    },
    {
      id: 'team-management',
      question: 'Can I manage my team in FlowDrizzle?',
      answer:
        'Yes. Admin users can manage team members, assign work, and keep CRM access organized for sales and management roles.',
      keywords: ['team', 'manager', 'managers', 'user', 'users', 'role', 'roles', 'access'],
      links: [
        { label: 'Team Management', url: '/admin/managers', description: 'Manage CRM team members.' },
      ],
    },
  ];

  getSuggestions(): string[] {
    return [
      'What is FlowDrizzle?',
      'What features does FlowDrizzle provide?',
      'Is FlowDrizzle suitable for enterprise teams?',
      'Can FlowDrizzle manage my sales pipeline?',
      'What automation is available?',
      'What are the pricing plans?',
    ];
  }

  ask(question: string): ChatbotAnswer {
    const normalizedQuestion = question.trim().toLowerCase();

    if (!normalizedQuestion) {
      return this.fallbackAnswer;
    }

    const queryTerms = this.toTerms(normalizedQuestion);

    const bestMatch = this.knowledgeBase
      .map((item) => ({
        item,
        score: this.getScore(item, normalizedQuestion, queryTerms),
      }))
      .sort((a, b) => b.score - a.score)[0];

    if (!bestMatch || bestMatch.score < 2) {
      return this.fallbackAnswer;
    }

    return {
      answer: bestMatch.item.answer,
      links: bestMatch.item.links,
      matchedQuestion: bestMatch.item.question,
    };
  }

  getMessages(): ChatbotMessage[] {
    return this.readFromStorage<ChatbotMessage[]>(this.messageStorageKey, []);
  }

  saveMessages(messages: ChatbotMessage[]): void {
    this.writeToStorage(this.messageStorageKey, messages.slice(-this.maxMessages));
  }

  clearMessages(): void {
    localStorage.removeItem(this.messageStorageKey);
  }

  getHistory(): ChatbotHistoryItem[] {
    return this.readFromStorage<ChatbotHistoryItem[]>(this.historyStorageKey, []);
  }

  saveHistoryItem(question: string, answer: ChatbotAnswer): ChatbotHistoryItem {
    const historyItem: ChatbotHistoryItem = {
      id: this.createId(),
      question,
      answer,
      createdAt: new Date().toISOString(),
    };

    const latestHistory = [
      historyItem,
      ...this.getHistory().filter((item) => item.question.toLowerCase() !== question.toLowerCase()),
    ].slice(0, this.maxHistoryItems);

    this.writeToStorage(this.historyStorageKey, latestHistory);

    return historyItem;
  }

  clearHistory(): void {
    localStorage.removeItem(this.historyStorageKey);
  }

  private getScore(item: ChatbotKnowledgeItem, normalizedQuestion: string, queryTerms: string[]): number {
    const itemQuestion = item.question.toLowerCase();
    const itemAnswer = item.answer.toLowerCase();

    const keywordScore = item.keywords.reduce((score, keyword) => {
      return normalizedQuestion.includes(keyword.toLowerCase()) ? score + 4 : score;
    }, 0);

    const questionScore = queryTerms.reduce((score, term) => {
      return itemQuestion.includes(term) ? score + 2 : score;
    }, 0);

    const answerScore = queryTerms.reduce((score, term) => {
      return itemAnswer.includes(term) ? score + 1 : score;
    }, 0);

    return keywordScore + questionScore + answerScore;
  }

  private toTerms(value: string): string[] {
    return value
      .split(/[^a-z0-9]+/)
      .map((term) => term.trim())
      .filter((term) => term.length > 2);
  }

  private readFromStorage<T>(key: string, fallback: T): T {
    try {
      const value = localStorage.getItem(key);
      return value ? (JSON.parse(value) as T) : fallback;
    } catch {
      return fallback;
    }
  }

  private writeToStorage<T>(key: string, value: T): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  private createId(): string {
    return typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;
  }
}
